/**
 * Shipping Calculator Utilities for V3
 *
 * Calculates shipping costs and free shipping thresholds based on country and cart total.
 * All amounts are in cents (e.g., 10000 = €100.00)
 */

export type ShippingCountry = 'AT' | 'DE' | 'EU' | 'OTHER'

export interface ShippingTier {
  maxWeightKg: number  // upper bound (exclusive); use Infinity for the last tier
  rate: number         // in cents
}

export interface ShippingConfig {
  country: ShippingCountry
  freeShippingThreshold: number // in cents
  tiers: ShippingTier[]
  flatRate: number // in cents
  name: string
}

// Shipping configurations by country
export const SHIPPING_CONFIGS: Record<ShippingCountry, ShippingConfig> = {
  AT: {
    country: 'AT',
    freeShippingThreshold: 15000, // €150
    flatRate: 800, // €8 fallback (lightest tier)
    tiers: [
      { maxWeightKg: 4, rate: 800 }, // €8 for 0–4 kg
      { maxWeightKg: Infinity, rate: 900 }, // €9 for 4–31.5 kg
    ],
    name: 'Austria',
  },
  DE: {
    country: 'DE',
    freeShippingThreshold: 25000, // €250
    flatRate: 1300, // €13 fallback (lightest tier)
    tiers: [
      { maxWeightKg: 2, rate: 1300 }, // €13 for 0–2 kg
      { maxWeightKg: Infinity, rate: 1400 }, // €14 for 2–31.5 kg
    ],
    name: 'Germany',
  },
  EU: {
    country: 'EU',
    freeShippingThreshold: 30000, // €300
    flatRate: 1500, // €15
    tiers: [
      { maxWeightKg: Infinity, rate: 1500 },
    ],
    name: 'European Union',
  },
  OTHER: {
    country: 'OTHER',
    freeShippingThreshold: 0, // No free shipping
    flatRate: 2500, // €25
    tiers: [
      { maxWeightKg: Infinity, rate: 2500 },
    ],
    name: 'International',
  },
}

export interface ShippingInfo {
  shippingCost: number
  isFree: boolean
  remainingForFreeShipping: number
  country: ShippingCountry
  freeShippingThreshold: number
  config: ShippingConfig
}

/**
 * Calculate shipping info based on cart total and country
 *
 * @param cartTotal - Cart subtotal in cents (excluding shipping)
 * @param country - Shipping destination country code
 * @param containsManualDeliveryItems - Whether cart contains items requiring manual delivery
 * @returns ShippingInfo object with all shipping details
 */
export function calculateShipping(
  cartTotal: number,
  country: ShippingCountry = 'AT',
  containsManualDeliveryItems: boolean = false,
  weightKg?: number | null,
): ShippingInfo {
  const config = SHIPPING_CONFIGS[country] || SHIPPING_CONFIGS.AT

  if (containsManualDeliveryItems) {
    return { shippingCost: 0, isFree: false, remainingForFreeShipping: 0, country, freeShippingThreshold: 0, config }
  }

  const { freeShippingThreshold } = config
  const isFree = freeShippingThreshold > 0 && cartTotal >= freeShippingThreshold
  const remainingForFreeShipping = freeShippingThreshold > 0 ? Math.max(0, freeShippingThreshold - cartTotal) : 0

  // Resolve rate: use weight-based tier if weightKg is provided, otherwise fall back to flatRate
  let rate: number
  if (weightKg != null && config.tiers.length > 0) {
    const tier = config.tiers.find(t => weightKg < t.maxWeightKg) ?? config.tiers[config.tiers.length - 1]
    rate = tier.rate
  } else {
    rate = config.flatRate
  }

  const shippingCost = isFree ? 0 : rate

  return { shippingCost, isFree, remainingForFreeShipping, country, freeShippingThreshold, config }
}

export function resolveItemWeight(
  productWeightKg?: number | null,
  variantWeightKg?: number | null,
): number | null {
  return variantWeightKg ?? productWeightKg ?? null
}

/**
 * Get country code from address or user locale
 *
 * @param countryCode - ISO country code (e.g., 'AT', 'DE', 'FR')
 * @returns Normalized ShippingCountry
 */
export function getShippingCountry(countryCode?: string): ShippingCountry {
  if (!countryCode) return 'AT'

  const normalized = countryCode.toUpperCase()

  if (normalized === 'AT') return 'AT'
  if (normalized === 'DE') return 'DE'

  // EU countries
  const euCountries = [
    'BE',
    'BG',
    'CZ',
    'DK',
    'EE',
    'IE',
    'EL',
    'ES',
    'FR',
    'HR',
    'IT',
    'CY',
    'LV',
    'LT',
    'LU',
    'HU',
    'MT',
    'NL',
    'PL',
    'PT',
    'RO',
    'SI',
    'SK',
    'FI',
    'SE',
  ]

  if (euCountries.includes(normalized)) return 'EU'

  return 'OTHER'
}

/**
 * Format currency amount from cents to display string
 *
 * @param amountInCents - Amount in cents
 * @param locale - Locale for formatting (default: 'de-AT')
 * @returns Formatted currency string
 */
export function formatShippingCurrency(amountInCents: number, locale: string = 'de-AT'): string {
  return new Intl.NumberFormat(locale, {
    style: 'currency',
    currency: 'EUR',
  }).format(amountInCents / 100)
}

/**
 * Calculate progress percentage towards free shipping
 *
 * @param cartTotal - Current cart total in cents
 * @param threshold - Free shipping threshold in cents
 * @returns Progress percentage (0-100)
 */
export function calculateFreeShippingProgress(cartTotal: number, threshold: number): number {
  if (threshold <= 0) return 0
  return Math.min(100, Math.round((cartTotal / threshold) * 100))
}

/**
 * The carrier rate for a country + weight, IGNORING the free-shipping threshold.
 *
 * `calculateShipping` returns 0 once an order qualifies for free shipping — but
 * the shop still pays the carrier. This returns what that parcel costs, so the
 * absorbed amount can be recorded on the order and split between the partners
 * at settlement. Falls back to the country's flat rate when weight is unknown.
 */
export function getBaseShippingCost(
  country: ShippingCountry = 'AT',
  weightKg?: number | null,
): number {
  const config = SHIPPING_CONFIGS[country] || SHIPPING_CONFIGS.AT

  if (weightKg != null && config.tiers.length > 0) {
    const tier = config.tiers.find((t) => weightKg < t.maxWeightKg) ?? config.tiers[config.tiers.length - 1]
    return tier.rate
  }

  return config.flatRate
}
